<!-- eslint-disable prettier/prettier -->
<script setup lang="ts">
import AppState, { contactLabelSingular } from '@/states/app'
import { Trash01Icon } from '@gohighlevel/ghl-icons/24/outline'
import {
  UIButton,
  UIDivider,
  UIFormItem,
  UIInput,
  UIRadio,
  UISwitch,
  UITooltip,
} from '@gohighlevel/ghl-ui'
import { computed, reactive, ref, watch, watchEffect } from 'vue'
import { useI18n } from 'vue-i18n'
import { CalendarService } from '../../class/services/CalendarService'
import { CalendarType } from '../../models/calendar'
import { getTooltip } from '../../utils/generalFunctions'
import DependentSettings from '../common/DependentSettings.vue'
import HeaderTile from '../common/HeaderTile.vue'
import ImageUpload from '../common/ImageUpload.vue'
import WidgetConfigurator from './WidgetConfigurator.vue'

const { t } = useI18n()

const widgetConfigurator = ref()
const state = reactive({
  isLoading: false,
  showMediaLibrary: false,
  mediaLibraryUsedForKey: '',
  isFormFirstEnabled: false,
  formData: {
    calendarCoverImage: '',
    widgetType: 'default',
    codeBlock: '',
    enableStaffSelection: false,
    showSeatsPerSlot: false,
  },
  fileList: [],
  showDependentSettings: false,
})
const editingCalendar = computed(() => {
  return AppState.editingCalendar
})

defineEmits(['onSave', 'onCancel'])

const methods = {
  async loadData() {
    state.formData.widgetType = editingCalendar.value?.widgetType
      ? editingCalendar.value?.widgetType
      : 'default'

    state.formData.showSeatsPerSlot = Boolean(
      editingCalendar.value?.showSeatsPerSlot
    )

    state.formData.codeBlock = editingCalendar.value?.codeBlock || ''

    state.formData.enableStaffSelection = Boolean(
      editingCalendar.value?.enableStaffSelection
    )
    state.formData.calendarCoverImage =
      editingCalendar.value?.calendarCoverImage || ''
    const pageOrder = editingCalendar.value?.widgetConfig?.pageOrder?.find(
      (x: any) => x.kind === 'form'
    )
    state.isFormFirstEnabled = pageOrder?.position === 0
  },
  emitSave() {
    const widgetConfig =
      widgetConfigurator.value.methods.getWidgetConfiguration()
    return {
      ...state.formData,
      widgetConfig: widgetConfig,
    }
  },
  async handleUploadFromSystem(file: any) {
    const formData = new FormData()
    formData.append('logo', file.file)
    const {
      data: { uploadedUrl },
    } = await CalendarService.uploadCalendarLogo(
      editingCalendar.value!.id,
      formData,
      editingCalendar?.value?.locationId
    )

    state.formData.calendarCoverImage = uploadedUrl
  },
  async removeLogo() {
    state.formData.calendarCoverImage = ''
  },
  toggleMediaLibrary() {
    methods.openMediaLibrary('calendarCoverImage')
  },
  openMediaLibrary(fieldType: string) {
    AppState.mediaLibraryFieldType = fieldType
    AppState.parentApp?.then(parent => {
      parent.emit('media-library-open', {
        apiKey: '',
        tokenId: AppState.tokenId,
        companyId: AppState.companyId,
        altId: AppState.locationId,
        altType: 'location',
        hideHeader: true,
        allowedExtensions: ['image'],
        maxUploadSize: 50000000,
        category: 'calendars',
        subCategory: 'calendar_settings',
      })
      AppState.isMediaLibraryOpen = true
    })
  },
}

watchEffect(() => {
  if (AppState.mediaFileURI) {
    if (AppState.mediaLibraryFieldType === 'calendarCoverImage') {
      state.formData.calendarCoverImage = AppState.mediaFileURI
      AppState.mediaLibraryFieldType = ''
      AppState.mediaFileURI = ''
    }
    // Clear the field type after use
  }
})

defineExpose({
  methods,
})

watch(
  () => state.formData.widgetType,
  () => {
    state.formData.enableStaffSelection =
      state.formData.widgetType === 'classic'
        ? false
        : state.formData.enableStaffSelection
    AppState.isClassicWidgetSelected = state.formData.widgetType === 'classic'
  }
)

watch(
  () => editingCalendar.value,
  () => {
    methods.loadData()
  },
  { immediate: true }
)

const isServiceCalendar = computed(() => {
  return editingCalendar.value?.isServiceCalendar
})

const showStaffSelection = computed(() => {
  return (
    editingCalendar.value?.isRoundRobinCalendar ||
    editingCalendar.value?.isServiceCalendar
  )
})

const classicDependentSettings = computed(() => {
  const settings: string[] = []
  if (AppState.isFormFirstEnabled) {
    settings.push(t('calendar_advanced.extras.form_first_disabled'))
  }

  if (AppState.editCalendarValidationData.enableSameUserAssignment) {
    settings.push(
      t('calendar_advanced.extras.appointment_preference_disabled', {
        contactLabel: contactLabelSingular.value,
      })
    )
  }

  if (AppState.isMultipleLocationEnabled) {
    settings.push(t('calendar_advanced.extras.multiple_location_disabled'))
  }
  return settings
})

const cancelDependentSettings = () => {
  state.showDependentSettings = false
  state.formData.widgetType = 'default'
}

const confirmDependentSettings = () => {
  state.showDependentSettings = false
  state.formData.widgetType = 'classic'
  if (AppState.isFormFirstEnabled) {
    AppState.editCalendarValidationData.disableFormFirst = true
  }
  if (AppState.editCalendarValidationData.enableSameUserAssignment) {
    AppState.editCalendarValidationData.enableSameUserAssignment = false
  }
  if (AppState.isMultipleLocationEnabled) {
    AppState.editCalendarValidationData.removeMultipleLocation = true
  }
}

const onChangeWidgetType = (value: string) => {
  state.formData.widgetType = value
  if (
    value === 'classic' &&
    (AppState.isFormFirstEnabled ||
      AppState.editCalendarValidationData.enableSameUserAssignment ||
      AppState.isMultipleLocationEnabled)
  ) {
    state.showDependentSettings = true
  } else {
    AppState.editCalendarValidationData.disableFormFirst = false
  }
}
</script>

<template>
  <div class="rounded-xl border bg-white p-5 !pr-[18px] shadow-sm">
    <HeaderTile
      id="text-only-header"
      :header="$t('calendar_advanced.extras.extras')"
      :description="
        isServiceCalendar
          ? $t('calendar_advanced.extras.service_additional_preferences')
          : $t('calendar_advanced.extras.extras_sub')
      "
    />
    <div class="my-[15px] border-t border-gray-200" />

    <div
      class="mb-4 flex flex-col border-b border-gray-200 pb-2"
      v-if="editingCalendar?.isClassBookingCalendar"
    >
      <div class="font-medium text-gray-900">
        {{ $t('calendar_advanced.extras.seats_per_slot') }}
      </div>
      <div class="text-xs font-normal leading-6 text-gray-600">
        {{ $t('calendar_advanced.extras.seats_per_slot_desc') }}
      </div>
      <div class="flex items-center space-x-3 pb-3 pt-4">
        <div id="show-seats-per-slot">
          <UISwitch
            v-model:value="state.formData.showSeatsPerSlot"
            :aria-label="t('aria_labels.show_seats_per_slot')"
            size="small"
          />
        </div>

        <div class="font-normal">
          {{ $t('calendar_advanced.extras.show_seats_per_slot') }}
        </div>
      </div>
    </div>

    <div
      v-if="showStaffSelection"
      class="mb-4 flex flex-col border-b border-gray-200 pb-2"
    >
      <div class="font-medium text-gray-900">
        {{ $t('calendar_advanced.extras.show_staff_selection') }}
      </div>
      <div class="text-xs font-normal leading-6 text-gray-600">
        {{
          isServiceCalendar
            ? $t('calendar_advanced.extras.show_staff_selection_desc_service')
            : $t('calendar_advanced.extras.show_staff_selection_desc')
        }}
      </div>
      <div class="flex items-center space-x-3 pb-3 pt-4">
        <div id="calendar-allow-staff-switch">
          <UITooltip
            placement="top"
            class="text-white"
            :trigger="
              state.formData.widgetType === 'classic' ? 'hover' : 'manual'
            "
          >
            <template #trigger>
              <div>
                <UISwitch
                  v-model:value="state.formData.enableStaffSelection"
                  :disabled="state.formData.widgetType === 'classic'"
                  size="small"
                />
              </div>
            </template>
            <p class="w-48 text-white">
              {{
                $t(
                  'calendar_advanced.extras.show_staff_selection_disabled_tooltip'
                )
              }}
            </p>
          </UITooltip>
        </div>
        <div class="font-normal">
          {{ $t('calendar_advanced.extras.allow_staff') }}
        </div>
      </div>
    </div>

    <div>
      <div class="flex flex-col">
        <div class="flex flex-row font-medium text-gray-900">
          {{
            isServiceCalendar
              ? $t('calendar_advanced.extras.service_image')
              : $t('calendar_advanced.extras.calendar_image')
          }}
        </div>
        <div class="mb-3 mt-1 text-xs text-gray-600">
          {{
            isServiceCalendar
              ? $t('calendar_advanced.extras.service_image_description')
              : $t('calendar_advanced.extras.calendar_image_info')
          }}
        </div>
        <div class="flex flex-col gap-1">
          <div class="flex flex-row">
            <ImageUpload
              class="w-full"
              v-if="!state.formData.calendarCoverImage"
              @upload-from-system="methods.handleUploadFromSystem"
              @choose-from-media-library="methods.toggleMediaLibrary"
            >
            </ImageUpload>
            <div v-else class="mt-3 flex items-center space-x-10">
              <img
                :src="state.formData.calendarCoverImage"
                class="h-40 w-40 rounded-lg object-cover"
                :alt="t('alt_text.calendar_cover_image')"
              />

              <div class="justify-centre flex flex-row">
                <UIButton
                  id="calendar-logo-remove-btn"
                  class="border-red-300 text-red-700"
                  @click="methods.removeLogo"
                >
                  <Trash01Icon class="mr-2 w-5" />
                  <div class="">
                    {{ $t('calendar_advanced.extras.remove_image') }}
                  </div>
                </UIButton>
              </div>
            </div>
          </div>
        </div>
      </div>
      <UIDivider />
    </div>

    <div v-if="!isServiceCalendar">
      <div class="mb-2">
        <div class="font-medium text-gray-900">
          {{ $t('calendar_advanced.extras.calendar_widget_style') }}
        </div>
        <div class="text-xs font-normal leading-6 text-gray-600">
          {{ $t('calendar_advanced.extras.calendar_widget_style_desc') }}
        </div>
      </div>
      <div class="flex w-full flex-col space-y-1">
        <div class="flex w-full flex-col space-y-4">
          <div class="flex space-x-4">
            <UIRadio
              id="calendar-default-widget"
              :checked="state.formData.widgetType === 'default'"
              value="default"
              name="default"
              @change="onChangeWidgetType('default')"
            >
              {{ $t('calendar_advanced.extras.neo') }}
            </UIRadio>
            <div class="flex items-center text-primary-600">
              <UITooltip
                :title="$t('calendar_advanced.extras.classic_tooltip_disabled')"
                placement="top"
                class="text-white"
                :trigger="
                  editingCalendar?.calendarType === CalendarType.CLASS_BOOKING
                    ? 'hover'
                    : 'manual'
                "
              >
                <template #trigger>
                  <UIRadio
                    id="calendar-classic-widget"
                    :checked="state.formData.widgetType === 'classic'"
                    value="classic"
                    name="classic"
                    :disabled="
                      editingCalendar?.calendarType ===
                      CalendarType.CLASS_BOOKING
                    "
                    @change="onChangeWidgetType('classic')"
                  >
                    {{ $t('calendar_advanced.extras.classic') }}
                  </UIRadio>
                </template>
                <p class="w-48 text-white">
                  {{ $t('calendar_advanced.extras.classic_tooltip_disabled') }}
                </p>
              </UITooltip>
            </div>
          </div>
        </div>
      </div>
    </div>

    <WidgetConfigurator ref="widgetConfigurator" />

    <UIFormItem
      :label="$t('calendar_advanced.extras.insert_custom_code')"
      class="mt-5 w-[430px]"
      :tooltip="
        getTooltip($t('calendar_advanced.extras.insert_custom_code_tooltip'))
      "
    >
      <div class="flex w-full flex-col space-y-4">
        <UIInput
          id="calendar-custom-codeBlock"
          v-model="state.formData.codeBlock"
          :rows="4"
          type="textarea"
          :placeholder="$t('calendar_advanced.extras.custom_code_placeholder')"
        />

        <div v-if="state.formData.codeBlock" class="text-yellow-500">
          {{ $t('calendar_advanced.extras.code_block_tooltip') }}
        </div>
      </div>
    </UIFormItem>

    <DependentSettings
      v-if="state.showDependentSettings"
      :show-modal="state.showDependentSettings"
      :title="$t('calendar_advanced.extras.switch_to_classic_widget')"
      :desc="$t('calendar_advanced.extras.switch_to_classic_widget_desc')"
      :settings="classicDependentSettings"
      @cancel-click="cancelDependentSettings"
      @confirm-click="confirmDependentSettings"
    />
  </div>
</template>
